import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { buildApiUrl, buildRequestHeaders } from '@dental-ui/shared/consts';
import { ResponseModel } from '@dental-ui/account/models';
import { FileTypeEnum, AttachmentDto } from '../models';
import { DocumentTypeEnum } from '../models/document-type-enum';

export interface AttachmentId {
  name: string;
  value: string;
}

@Injectable({
  providedIn: 'root',
})
export class AttachmentApiService {
  private readonly http = inject(HttpClient);

  /**
   * Endpoint path'inden tam API URL'i oluşturur
   * @param endpointPath - Nokta ile ayrılmış endpoint path'i (örn: 'attachment.upload')
   * @returns Tam URL string'i
   */
  protected buildApiUrl(endpointPath: string): string {
    return buildApiUrl(endpointPath);
  }

  /**
   * Attachment upload için özel header'ları oluşturur
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği
   * @returns HttpHeaders objesi
   */
  private buildAttachmentHeaders(authorize: boolean): HttpHeaders {
    const headers = buildRequestHeaders(authorize);
    // File upload için Content-Type header'ını kaldır (browser otomatik ekleyecek)
    return headers.delete('Content-Type');
  }

  /**
   * FormData oluşturur (kod tekrarını önlemek için)
   * @param file - Yüklenecek dosya
   * @param id - Dosya ile ilişkilendirilecek ID bilgisi
   * @param type - Dosya tipi
   * @param isStatic - Statik dosya olup olmadığı
   * @param description - Dosya açıklaması (isteğe bağlı)
   * @returns FormData objesi
   */
  private createFormData(
    file: File,
    id: AttachmentId,
    type: FileTypeEnum,
    documentType: DocumentTypeEnum,
    isStatic: boolean,
    description?: string
  ): FormData {
    const formData = new FormData();
    const now = new Date();
    const dateString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    formData.append('modifiedOn', dateString);
    formData.append('modifiedBy', '0');
    formData.append('type', type.toString());
    formData.append('documentType', documentType.toString());
    formData.append('date', dateString);
    formData.append('file', file, file.name);
    formData.append('isDeleted', 'false');
    formData.append('isStatic', isStatic.toString());

    if (description) {
      formData.append('description', description);
    }

    if (!isStatic) {
      if (!id.name || !id.value) {
        throw new Error('ID name and value are required for non-static files');
      }
      formData.append(id.name, id.value);
    }

    return formData;
  }

  /**
   * Dosya yükler
   * @param file - Yüklenecek dosya
   * @param id - Dosya ile ilişkilendirilecek ID bilgisi
   * @param type - Dosya tipi
   * @param isStatic - Statik dosya olup olmadığı (varsayılan: false)
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: true)
   * @param description - Dosya açıklaması (isteğe bağlı)
   * @returns Observable<ResponseModel<unknown>>
   */
  uploadFile(
    file: File,
    id: AttachmentId,
    type: FileTypeEnum,
    documentType: DocumentTypeEnum,
    isStatic = false,
    authorize = true,
    description?: string
  ): Observable<ResponseModel<unknown>> {
    const formData = this.createFormData(file, id, type, documentType, isStatic, description);

    return this.http.post<ResponseModel<unknown>>(
      this.buildApiUrl('attachment.upload'),
      formData,
      {
        headers: this.buildAttachmentHeaders(authorize),
        reportProgress: true,
      }
    );
  }

  /**
   * Şifreli dosya yükler
   * @param file - Yüklenecek dosya
   * @param id - Dosya ile ilişkilendirilecek ID bilgisi
   * @param type - Dosya tipi
   * @param isStatic - Statik dosya olup olmadığı (varsayılan: false)
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: true)
   * @returns Observable<ResponseModel<unknown>>
   */
  uploadFileWithCrypto(
    file: File,
    id: AttachmentId,
    type: FileTypeEnum,
    documentType: DocumentTypeEnum,
    isStatic = false,
    authorize = true
  ): Observable<ResponseModel<unknown>> {
    const formData = this.createFormData(file, id, type, documentType, isStatic);

    return this.http.post<ResponseModel<unknown>>(
      this.buildApiUrl('attachment.uploadCrypto'),
      formData,
      {
        headers: this.buildAttachmentHeaders(authorize),
        reportProgress: true,
      }
    );
  }

  /**
   * Ek dosyayı siler
   * @param attachmentId - Silinecek ek dosya ID'si
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: true)
   * @returns Observable<ResponseModel<boolean>>
   */
  deleteAttachment(
    attachmentId: string,
    authorize = true
  ): Observable<ResponseModel<boolean>> {
    const params = new HttpParams().set('attachmentId', attachmentId);

    return this.http.delete<ResponseModel<boolean>>(
      this.buildApiUrl('attachment.delete'),
      {
        headers: buildRequestHeaders(authorize),
        params,
      }
    );
  }

  /**
   * Dosya adına göre Azure'dan dosya getirir
   * @param fileName - Dosya adı
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: true)
   * @returns Observable<ResponseModel<unknown>>
   */
  getByFileName(
    fileName: string,
    authorize = true
  ): Observable<ResponseModel<unknown>> {
    const params = new HttpParams().set('fileName', fileName);

    return this.http.get<ResponseModel<unknown>>(
      this.buildApiUrl('attachment.getByFileName'),
      {
        headers: buildRequestHeaders(authorize),
        params,
      }
    );
  }

  /**
   * Aktif durum ve tip'e göre ek dosyaları getirir
   * @param isActive - Aktif durumu
   * @param type - Dosya tipi
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: true)
   * @returns Observable<ResponseModel<unknown>>
   */
  getAttachmentByIsActiveAndType(
    isActive: boolean,
    type: FileTypeEnum,
    authorize = true
  ): Observable<ResponseModel<unknown>> {
    const params = new HttpParams()
      .set('isActive', isActive.toString())
      .set('type', type.toString());

    return this.http.get<ResponseModel<unknown>>(
      this.buildApiUrl('attachment.getByIsActiveAndType'),
      {
        headers: buildRequestHeaders(authorize),
        params,
      }
    );
  }

  /**
   * Hasta ID ve tip'e göre ek dosyaları getirir
   * @param patientId - Hasta ID'si
   * @param type - Dosya tipi
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: true)
   * @returns Observable<ResponseModel<AttachmentDto | AttachmentDto[]>>
   */
  getAllAttachmentByPatientIdAndType(
    patientId: string,
    type: FileTypeEnum,
    authorize = true
  ): Observable<ResponseModel<AttachmentDto | AttachmentDto[]>> {
    const params = new HttpParams()
      .set('patient', patientId)
      .set('type', type.toString());

    return this.http.get<ResponseModel<AttachmentDto | AttachmentDto[]>>(
      this.buildApiUrl('attachment.getAllByPatientIdAndType'),
      {
        headers: buildRequestHeaders(authorize),
        params,
      }
    );
  }

  /**
   * İş emri ID ve tip'e göre ek dosyaları getirir
   * @param workOrderId - İş emri ID'si
   * @param type - Dosya tipi
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: true)
   * @returns Observable<ResponseModel<AttachmentDto | AttachmentDto[]>>
   */
  getAllAttachmentByWorkOrderIdAndType(
    workOrderId: string,
    type: FileTypeEnum,
    authorize = true
  ): Observable<ResponseModel<AttachmentDto | AttachmentDto[]>> {
    const params = new HttpParams()
      .set('workOrder', workOrderId)
      .set('type', type.toString());

    return this.http.get<ResponseModel<AttachmentDto | AttachmentDto[]>>(
      this.buildApiUrl('attachment.getAllByWorkOrderIdAndType'),
      {
        headers: buildRequestHeaders(authorize),
        params,
      }
    );
  }

  /**
   * QR ile dosya yükler (kimlik doğrulaması gerektirmez, token ile işlem yapar)
   * @param file - Yüklenecek dosya
   * @param patientId - Hasta ID'si
   * @param workOrderId - İş emri ID'si
   * @param type - Dosya tipi
   * @param documentType - Belge tipi
   * @param token - QR doğrulama token'ı
   * @returns Observable<ResponseModel<unknown>>
   */
  uploadFileWithQr(
    file: File,
    patientId: string | null,
    workOrderId: string | null,
    type: FileTypeEnum,
    documentType: DocumentTypeEnum,
    token: string
  ): Observable<ResponseModel<unknown>> {
    const formData = new FormData();
    const now = new Date();
    const dateString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    formData.append('modifiedOn', dateString);
    formData.append('modifiedBy', '0');
    if (patientId !== null) {
      formData.append('patientId', patientId);
    }
    if (workOrderId !== null) {
      formData.append('workOrderId', workOrderId);
    }
    formData.append('documentType', documentType.toString());
    formData.append('type', type.toString());
    formData.append('date', dateString);
    formData.append('File', file, file.name);
    formData.append('isDeleted', 'false');
    formData.append('token', token);

    const headers = this.buildAttachmentHeaders(false);

    return this.http.post<ResponseModel<unknown>>(
      this.buildApiUrl('attachment.addAttachmentWithQr'),
      formData,
      {
        headers,
      }
    );
  }
}
